import { Box, Select, Text, type SelectProps } from '@chakra-ui/react';
import { Children, forwardRef, isValidElement, useId, type ReactNode } from 'react';

type FloatingLabelSelectProps = Omit<SelectProps, 'placeholder' | 'value' | 'defaultValue'> & {
  label: string;
  value: string;
};

const findOptionLabel = (children: ReactNode, value: string): ReactNode => {
  for (const child of Children.toArray(children)) {
    if (!isValidElement<{ value?: string | number; children?: ReactNode }>(child)) continue;
    if (child.type === 'option' && String(child.props.value) === value) return child.props.children;
    const nestedLabel = findOptionLabel(child.props.children, value);
    if (nestedLabel != null) return nestedLabel;
  }
  return null;
};

const FloatingLabelSelect = forwardRef<HTMLSelectElement, FloatingLabelSelectProps>(
  ({ label, value, id, width = '100%', children, isDisabled, ...props }, ref) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const hasValue = value !== '';

    return (
      <Box position={'relative'} width={width}>
        <Select
          ref={ref}
          id={selectId}
          value={value}
          isDisabled={isDisabled}
          variant={'unstyled'}
          height={'54px'}
          width={'100%'}
          background={'white'}
          border={'1px solid'}
          borderColor={hasValue ? 'text.mediumBlue' : 'transparent'}
          borderRadius={'10px'}
          fontSize={'16px'}
          fontWeight={600}
          color={'transparent'}
          icon={<span />}
          boxShadow={'0px 2px 4px 0px #0041790D'}
          _focusVisible={{ borderColor: 'text.mediumBlue', boxShadow: '0 0 0 1px #004179' }}
          _invalid={{ borderColor: 'extra.red', boxShadow: '0 0 0 1px var(--chakra-colors-extra-red)' }}
          sx={{ '& option, & optgroup': { color: 'text.mediumBlue', background: 'white' } }}
          {...props}
        >
          <option value="" disabled hidden />
          {children}
        </Select>
        <Box
          position={'absolute'}
          inset={'1px'}
          paddingX={'18px'}
          paddingRight={'44px'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          pointerEvents={'none'}
          opacity={isDisabled ? 0.4 : 1}
        >
          <Text
            as={'label'}
            htmlFor={selectId}
            color={'background.blueGray'}
            fontSize={hasValue ? '11px' : '16px'}
            lineHeight={hasValue ? '15px' : '20px'}
            fontWeight={hasValue ? 800 : 600}
            textTransform={hasValue ? 'uppercase' : 'none'}
          >
            {label}
          </Text>
          {hasValue && (
            <Text
              aria-hidden
              color={'text.mediumBlue'}
              fontSize={'16px'}
              lineHeight={'20px'}
              fontWeight={600}
              isTruncated
            >
              {findOptionLabel(children, value) ?? value}
            </Text>
          )}
        </Box>
        <Box
          as={'svg'}
          viewBox={'0 0 16 16'}
          width={'16px'}
          height={'16px'}
          position={'absolute'}
          right={'22px'}
          top={'50%'}
          transform={'translateY(-50%)'}
          color={'background.blueGray'}
          opacity={isDisabled ? 0.4 : 1}
          pointerEvents={'none'}
          aria-hidden
        >
          <path
            d="M3.5 6L8 10.5L12.5 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
    );
  },
);

FloatingLabelSelect.displayName = 'FloatingLabelSelect';

export default FloatingLabelSelect;
